import React, { use, useCallback, useMemo, type AnchorHTMLAttributes, type FC, type PropsWithChildren } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";

import { AppRoutes } from "@domain/routing/routeConstants";
import { AppSettingsContext } from "@domain/context/AppSettingsProvider";
import { CompanyAccountingYearContext, CompanyContext } from "@domain/context/CompanyContext";
import { ReadinessContext } from "@domain/context/DataReadyness";
import { UserContext } from "@domain/context/UserProvider";
import { GQL } from "@binale-tech/shared";
import { type GroupKey, ProductDetails, ProductGroups, ProductKeys } from "@domain/models/Product";
import { ProductAccessUtils } from "@domain/models/utils/ProductAccessUtils";
import {
    ArrowUpFromLine,
    Bars,
    Calendar,
    ChartColumn,
    Gear,
    Heart,
    LayoutCellsLarge,
    Magnifier,
    Receipt,
    ScalesBalanced,
} from "@gravity-ui/icons";
import {
    Button,
    cn,
    Drawer,
    Header,
    type Key,
    ListBox,
    Popover,
    ScrollShadow,
    Spinner,
    useOverlayState,
} from "@heroui/react";
import { ProductColors } from "@ui-components/ProductColors";

const preventDefault = (e: React.MouseEvent<HTMLAnchorElement>) => e.preventDefault();

const LEGAL_LINKS = [
    { route: AppRoutes.impressum, messageId: "app.page.legal.impressum" },
    { route: AppRoutes.datenschutz, messageId: "app.page.legal.datenschutz" },
    { route: AppRoutes.agb, messageId: "app.page.legal.agb" },
    { route: AppRoutes.haftungsausschluss, messageId: "app.page.legal.haftungsausschluss" },
    { route: AppRoutes.cookies, messageId: "app.page.legal.cookiePolicy" },
    { route: AppRoutes.avv, messageId: "app.page.legal.avv" },
] as const;

const LinkWrapper: FC<PropsWithChildren<AnchorHTMLAttributes<HTMLAnchorElement>>> = ({ children, ...props }) => {
    return (
        <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            onClick={preventDefault}
            className="flex items-center gap-2"
        >
            {children}
        </a>
    );
};

export const AppsDrawer: FC = () => {
    const user = use(UserContext);
    const { isReady } = use(ReadinessContext);
    const { companyGQL } = use(CompanyContext);
    const { yearConfig } = use(CompanyAccountingYearContext);
    const { appError } = use(AppSettingsContext);

    const intl = useIntl();
    const navigate = useNavigate();
    const { pathname = "" } = useLocation();
    const drawerState = useOverlayState();

    const hasAccountingCommon = ProductAccessUtils.canRead(ProductKeys.AccountingCommon, companyGQL, user);

    const generateUrl = (key: string) => location.origin + key;

    const onDrawerOpenChange = useCallback(
        (open: boolean) => {
            if (!open) {
                drawerState.close();
            }
        },
        [drawerState],
    );

    const onListAction = useCallback(
        (key: Key) => {
            drawerState.close();
            navigate(key.toString());
        },
        [drawerState, navigate],
    );

    const disabledKeys = useMemo(() => {
        const keys = new Set<string>();
        const addGroupKeys = (gk: GroupKey) =>
            ProductDetails.getGroup(gk).forEach((d) => {
                if (!isReady || !ProductAccessUtils.canRead(d.pk, companyGQL, user)) {
                    keys.add(d.link);
                }
            });
        [
            ProductGroups.Buch,
            ProductGroups.AVM,
            ProductGroups.Contacts,
            ProductGroups.DMS,
            ProductGroups.Banks,
            ProductGroups.Invoices,
            ProductGroups.CashRegisters,
            ProductGroups.PDF,
        ].forEach(addGroupKeys);
        if (!hasAccountingCommon) {
            [
                AppRoutes.accountsView,
                AppRoutes.calendar,
                AppRoutes.SuSa,
                AppRoutes.BWA,
                AppRoutes.UStVA + AppRoutes.UStVAOverview,
                AppRoutes.recordImportExport,
                AppRoutes.filtersAndSearchView,
                AppRoutes.manageAccount,
            ].forEach((k) => keys.add(k));
        }
        return keys;
    }, [isReady, companyGQL, user, hasAccountingCommon]);

    const selectedKeys = useMemo(() => new Set([pathname.split("/").slice(0, 2).join("/")]), [pathname]);

    const itemClass =
        "rounded-2xl text-foreground data-[selected=true]:bg-accent/10 data-[selected=true]:text-accent data-[hovered=true]:bg-default/60 cursor-pointer transition-all duration-200";

    const groupGradients: Partial<Record<GroupKey, string>> = {
        [ProductGroups.DMS]: ProductColors.DMSGradient.className,
        [ProductGroups.Contacts]: ProductColors.ContactsGradient.className,
    };

    const getGroupItems = (gk: GroupKey) => {
        const gradientClass = groupGradients[gk];

        return ProductDetails.getGroup(gk).map((d) => (
            <ListBox.Item
                key={d.link}
                id={d.link}
                textValue={d.pk}
                className={cn(itemClass, gradientClass, gradientClass && "hover:brightness-110 hover:shadow-xl")}
            >
                <LinkWrapper href={generateUrl(d.link)} data-testid={`header-menu-link-${gk}-${d.pk}`}>
                    <span className={cn("text-accent", gradientClass && "text-inherit")}>{d.icon}</span>
                    <FormattedMessage id={`app.titles.${d.pk}`} tagName="span" />
                </LinkWrapper>
            </ListBox.Item>
        ));
    };

    return (
        <>
            <Button
                isIconOnly
                variant="outline"
                className="rounded-full bg-app-surface mr-2"
                isDisabled={Boolean(appError) || !user.isAuthenticated || !user?.fireUser?.emailVerified}
                isPending={user.isLoading}
                onPress={drawerState.open}
                data-testid="header-menu-btn"
            >
                {({ isPending }) => (isPending ? <Spinner color="current" size="sm" /> : <Bars className="size-4" />)}
            </Button>
            <Drawer.Backdrop isOpen={drawerState.isOpen} onOpenChange={onDrawerOpenChange}>
                <Drawer.Content placement="left" data-testid="header-menu-drawer">
                    <Drawer.Dialog
                        className="bg-app-surface"
                        aria-label={intl.formatMessage({ id: "app.header.navigation" })}
                    >
                        <Drawer.Body className="p-0">
                            <ScrollShadow className="h-full px-2 py-2">
                                <ListBox
                                    aria-label={intl.formatMessage({ id: "app.header.navigation" })}
                                    className=" mb-10 gap-0 [&>section+section]:border-t [&>section+section]:border-default/40 [&>section+section]:mt-1 [&>section+section]:pt-1"
                                    onAction={onListAction}
                                    selectedKeys={selectedKeys}
                                    disabledKeys={disabledKeys}
                                >
                                    <ListBox.Section>
                                        <ListBox.Item id={AppRoutes.home} textValue="Home" className={itemClass}>
                                            <LinkWrapper
                                                href={generateUrl(AppRoutes.home)}
                                                data-testid="header-menu-link-home"
                                            >
                                                <span className="text-accent">
                                                    <Heart className="size-4" />
                                                </span>
                                                <span className="font-semibold">
                                                    <FormattedMessage id="app.header.workplace" />
                                                </span>
                                            </LinkWrapper>
                                        </ListBox.Item>
                                    </ListBox.Section>

                                    <ListBox.Section>{getGroupItems(ProductGroups.DMS)}</ListBox.Section>

                                    <ListBox.Section>
                                        <Header>
                                            <FormattedMessage id="app.titles.buchungen" />
                                        </Header>
                                        {getGroupItems(ProductGroups.Buch)}
                                    </ListBox.Section>

                                    <ListBox.Section>
                                        <Header>
                                            <FormattedMessage id="app.titles.ER.auswertung" />
                                        </Header>
                                        <ListBox.Item id={AppRoutes.accountsView} textValue="KA" className={itemClass}>
                                            <LinkWrapper href={generateUrl(AppRoutes.accountsView)}>
                                                <span className="text-accent">
                                                    <LayoutCellsLarge className="size-4" />
                                                </span>
                                                <FormattedMessage id="app.titles.KA" tagName="span" />
                                            </LinkWrapper>
                                        </ListBox.Item>
                                        <ListBox.Item id={AppRoutes.SuSa} textValue="SuSa" className={itemClass}>
                                            <LinkWrapper href={generateUrl(AppRoutes.SuSa)}>
                                                <span className="text-accent">
                                                    <ChartColumn className="size-4" />
                                                </span>
                                                <FormattedMessage id="app.titles.SuSa" tagName="span" />
                                            </LinkWrapper>
                                        </ListBox.Item>
                                        <ListBox.Item id={AppRoutes.BWA} textValue="BWA" className={itemClass}>
                                            <LinkWrapper href={generateUrl(AppRoutes.BWA)}>
                                                <span className="text-accent">
                                                    <ChartColumn className="size-4" />
                                                </span>
                                                <span>BWA</span>
                                            </LinkWrapper>
                                        </ListBox.Item>
                                        {yearConfig?.taxation !== GQL.ICompanyTaxation.Kust ? (
                                            <ListBox.Item
                                                id={AppRoutes.UStVA + AppRoutes.UStVAOverview}
                                                textValue="UStVA"
                                                className={itemClass}
                                            >
                                                <LinkWrapper
                                                    href={generateUrl(AppRoutes.UStVA + AppRoutes.UStVAOverview)}
                                                >
                                                    <span className="text-accent">
                                                        <Receipt className="size-4" />
                                                    </span>
                                                    <FormattedMessage id="app.titles.Vat" tagName="span" />
                                                </LinkWrapper>
                                            </ListBox.Item>
                                        ) : null}
                                        <ListBox.Item
                                            id={AppRoutes.recordImportExport}
                                            textValue="Import/Export"
                                            className={itemClass}
                                        >
                                            <LinkWrapper href={generateUrl(AppRoutes.recordImportExport)}>
                                                <span className="text-accent">
                                                    <ArrowUpFromLine className="size-4" />
                                                </span>
                                                <FormattedMessage id="app.titles.import_export" tagName="span" />
                                            </LinkWrapper>
                                        </ListBox.Item>
                                        <ListBox.Item
                                            id={AppRoutes.filtersAndSearchView}
                                            textValue="Filter"
                                            className={itemClass}
                                        >
                                            <LinkWrapper href={generateUrl(AppRoutes.filtersAndSearchView)}>
                                                <span className="text-accent">
                                                    <Magnifier className="size-4" />
                                                </span>
                                                <FormattedMessage id="app.titles.filtern" tagName="span" />
                                            </LinkWrapper>
                                        </ListBox.Item>
                                        <ListBox.Item
                                            id={AppRoutes.manageAccount}
                                            textValue="Kontenverwaltung"
                                            className={itemClass}
                                        >
                                            <LinkWrapper href={generateUrl(AppRoutes.manageAccount)}>
                                                <span className="text-accent">
                                                    <Gear className="size-4" />
                                                </span>
                                                <FormattedMessage id="app.titles.kontenverwaltung" tagName="span" />
                                            </LinkWrapper>
                                        </ListBox.Item>
                                    </ListBox.Section>

                                    <ListBox.Section>
                                        <Header>
                                            <FormattedMessage id="app.header.others" />
                                        </Header>
                                        {getGroupItems(ProductGroups.Contacts)}
                                        {getGroupItems(ProductGroups.Banks)}
                                        {getGroupItems(ProductGroups.CashRegisters)}
                                        {getGroupItems(ProductGroups.Invoices)}
                                        {getGroupItems(ProductGroups.AVM)}

                                        <ListBox.Item
                                            id={AppRoutes.calendar}
                                            textValue="Kalender"
                                            className={itemClass}
                                        >
                                            <LinkWrapper href={generateUrl(AppRoutes.calendar)}>
                                                <span className="text-accent">
                                                    <Calendar className="size-4" />
                                                </span>
                                                <FormattedMessage id="app.titles.calendar" tagName="span" />
                                            </LinkWrapper>
                                        </ListBox.Item>

                                        {getGroupItems(ProductGroups.PDF)}
                                    </ListBox.Section>
                                </ListBox>

                                {/* Legal pages must stay reachable from every screen - the public
                                    footers only exist on the home and auth views. Six of them
                                    crowded out the products they sit under, so they collapse into
                                    one entry that opens them beside the drawer.

                                    Deliberately OUTSIDE the ListBox above: a Popover.Trigger inside
                                    a ListBox.Item would fight the listbox for the click, since
                                    selection and `onAction` already own it. The popover carries its
                                    own ListBox so the links keep keyboard navigation and reuse
                                    `onListAction`, which closes the drawer and routes. */}
                                <Popover>
                                    <Popover.Trigger
                                        tabIndex={-1}
                                        className={cn(
                                            itemClass,
                                            "mt-1 flex w-full items-center gap-2 py-1.5 text-left",
                                        )}
                                        aria-label={intl.formatMessage({ id: "app.page.legal.compliance" })}
                                    >
                                        <Button variant="secondary" className="w-full mx-2">
                                            <ScalesBalanced className="size-4" />
                                            <FormattedMessage id="app.page.legal.compliance" tagName="span" />
                                        </Button>
                                    </Popover.Trigger>
                                    <Popover.Content placement="right" className="min-w-64">
                                        <Popover.Dialog
                                            aria-label={intl.formatMessage({ id: "app.page.legal.compliance" })}
                                        >
                                            <ListBox aria-label="Legal" onAction={onListAction}>
                                                {LEGAL_LINKS.map(({ route, messageId }) => (
                                                    <ListBox.Item
                                                        key={route}
                                                        id={route}
                                                        textValue={intl.formatMessage({ id: messageId })}
                                                        className={itemClass}
                                                    >
                                                        <LinkWrapper href={generateUrl(route)}>
                                                            <span className="text-accent">
                                                                <ScalesBalanced className="size-4" />
                                                            </span>
                                                            <FormattedMessage id={messageId} tagName="span" />
                                                        </LinkWrapper>
                                                    </ListBox.Item>
                                                ))}
                                            </ListBox>
                                        </Popover.Dialog>
                                    </Popover.Content>
                                </Popover>
                            </ScrollShadow>
                        </Drawer.Body>
                    </Drawer.Dialog>
                </Drawer.Content>
            </Drawer.Backdrop>
        </>
    );
};
